import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui';
import { HomeMedia } from '@/components/home/HomeMedia';
import { formatEventDate } from '@/lib/format';
import { useMoney } from '@/hooks/useMoney';
import { localizeEventContent, type EventListItem } from '@/hooks/useEvents';

/**
 * HomeEventCard — variante VISUAL del `EventCard` exclusiva del landing (`/`).
 *
 * ¿Por qué una variante local y no editar `EventCard`? Ese componente lo
 * comparte también `/eventos`; para no alterar esa vista, el Home usa su
 * propia card. Diferencias respecto a la compartida:
 *   - Usa la imagen real del evento (`coverImage`) vía `HomeMedia`, con
 *     carga diferida y fallback de marca.
 *   - La card completa es un único enlace accesible (un solo foco) al detalle.
 *   - Hover con elevación + zoom sutil de la imagen.
 *
 * Consume EXACTAMENTE los mismos datos, i18n (`events`) y formateadores que la
 * card original — cero cambios de lógica, datos o rutas.
 */

function ArrowIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

export function HomeEventCard({ event }: { event: EventListItem }): JSX.Element {
  const { t, i18n } = useTranslation('events');
  const { moneyCents } = useMoney();
  const localized = localizeEventContent(event, i18n.language);

  return (
    <Link
      to={`/event/${event.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper shadow-sm transition duration-200 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <HomeMedia src={event.coverImage} alt={localized.title}>
        <Badge tone="success" className="absolute left-3 top-3 shadow-sm">
          {t('card.available')}
        </Badge>
      </HomeMedia>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 font-display text-lg font-semibold text-fc-ink">
          {localized.title}
        </h3>
        {event.date && (
          <p className="mt-1.5 text-sm text-fc-muted">{formatEventDate(event.date)}</p>
        )}
        {event.venue && <p className="line-clamp-1 text-sm text-fc-muted">{event.venue}</p>}

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-fc-line/70 pt-4">
          <span className="text-sm text-fc-muted">
            {t('card.from')}{' '}
            <span className="block font-display text-base font-semibold tabular-nums text-fc-ink">
              {moneyCents(event.priceCents, event.currency)}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-transform duration-200 group-hover:translate-x-0.5">
            {t('card.viewDetail')}
            <ArrowIcon />
          </span>
        </div>
      </div>
    </Link>
  );
}
